import { Elements } from "@stripe/react-stripe-js";
import React, { useState } from "react";
import type Stripe from "stripe";

import { ICompanyBillingSettingsFrontend } from "@performyard/models";

import { AdminContainer } from "../../../components/Admin";
import Button from "../../../components/Button";
import Spinner from "../../../components/Spinner";
import Text from "../../../components/Text";
import Typography from "../../../components/Text";
import BillingHistory from "./BillingHistory";
import CreditCardNumber from "./CreditCardNumber";

import {
	useCancelCreditCardSetupIntent,
	useCompanyBillingInfo,
	useCreateCreditCardSetupIntent,
	useCurrentUser,
} from "../../../api/hooks";

import BasePageTemplate from "../../../pageTemplates/BasePageTemplate";
import { stripePromise } from "../stripeConfig";
import AddUsersModal from "./modals/AddUsersModal";
import CreditCardModal from "./modals/CreditCardModal";

interface ModalsState {
	addUsers: boolean;
	creditCard: boolean;
}

const PageHeader = ({
	company,
	userCompanyId,
}: {
	company?: ICompanyBillingSettingsFrontend;
	userCompanyId?: string;
}) => (
	<Text element="h1" size="heading-5" weight="bold" color="neutral-9" className="mt-0">
		Billing Information
		{company?._id && userCompanyId !== company?._id && ` - ${company.name}`}
	</Text>
);

const CreditCardSection = ({
	card,
	onOpenCardModal,
	isCreateCreditCardSetupIntentPending,
}: {
	card?: Stripe.PaymentMethod | null;
	onOpenCardModal: () => void;
	isCreateCreditCardSetupIntentPending: boolean;
}) => {
	if (!card) return null;

	return (
		<div className="space-y-4">
			<CreditCardNumber paymentMethod={card} />
			<Button color="gray" onClick={onOpenCardModal} isRequesting={isCreateCreditCardSetupIntentPending}>
				Change
			</Button>
		</div>
	);
};

const ActionSection = ({
	canAddSeats,
	onOpenCardModal,
	onOpenAddUsersModal,
	isCreateCreditCardSetupIntentPending,
}: {
	canAddSeats: {
		hasPaymentMethod: boolean;
		hasValidPricing: boolean;
		hasValidBillingDate: boolean;
		hasTaxInfo: boolean;
	};
	onOpenCardModal: () => void;
	onOpenAddUsersModal: () => void;
	isCreateCreditCardSetupIntentPending: boolean;
}) => {
	if (
		canAddSeats.hasPaymentMethod &&
		canAddSeats.hasValidPricing &&
		canAddSeats.hasValidBillingDate &&
		canAddSeats.hasTaxInfo
	) {
		return (
			<div>
				<Button className="mt-5" onClick={onOpenAddUsersModal}>
					Add More Seats
				</Button>
			</div>
		);
	}

	if (canAddSeats.hasValidPricing && canAddSeats.hasValidBillingDate && canAddSeats.hasTaxInfo) {
		return (
			<div className="space-y-3">
				<Typography size="paragraph-2" weight="regular">
					To add new seats for PerformYard, please add your credit card.
				</Typography>
				<Typography size="paragraph-2" weight="regular">
					If you have any questions, feel free to contact us at{" "}
					<a href="mailto:support@performyard.com">support@performyard.com</a> or 703-870-3710.
				</Typography>
				<Button onClick={onOpenCardModal} isRequesting={isCreateCreditCardSetupIntentPending}>
					Add
				</Button>
			</div>
		);
	}

	return (
		<Typography size="paragraph-2" weight="regular" className="mt-5">
			To purchase PerformYard or add new seats for PerformYard, please visit our{" "}
			<a href="https://www.performyard.com/contact" target="_blank" rel="noreferrer">
				Contact Page
			</a>{" "}
			or contact us at <a href="mailto:support@performyard.com">support@performyard.com</a>.
		</Typography>
	);
};

const Billing = () => {
	const [modals, setModals] = useState<ModalsState>({ addUsers: false, creditCard: false });
	const [setupIntent, setSetupIntent] = useState<Stripe.SetupIntent | null>(null);

	const { data: user, isLoading: isUserLoading } = useCurrentUser();
	const { data: billingData, isLoading: isBillingDataLoading } = useCompanyBillingInfo();

	const { mutate: createSetupIntent, isPending: isCreateCreditCardSetupIntentPending } =
		useCreateCreditCardSetupIntent(setSetupIntent);
	const { mutate: cancelSetupIntent } = useCancelCreditCardSetupIntent(setSetupIntent);

	const toggleModal = (modalName: keyof ModalsState) => {
		setModals((prev) => ({ ...prev, [modalName]: !prev[modalName] }));
	};

	const handleOpenCardModal = async () => {
		createSetupIntent();
		toggleModal("creditCard");
	};

	const handleCloseCardModal = (canceled: boolean = false) => {
		if (canceled && setupIntent?.id && setupIntent.status !== "canceled") {
			cancelSetupIntent(setupIntent.id);
		}
		toggleModal("creditCard");
	};

	if (isUserLoading || isBillingDataLoading) {
		return <Spinner />;
	}

	if (!billingData) {
		throw new Error("Error loading billing data");
	}

	const { company, card, charges = [] } = billingData || {};
	const userCompanyId = user?.me?.authorization?.payload?.company_id;

	const canAddSeats = company && {
		hasPaymentMethod: Boolean(card),
		hasValidPricing: Boolean(
			company.min_users_to_add && company.min_users_to_add >= 0 && company.price_per_user > 0
		),
		hasValidBillingDate: Boolean(company.billing_expires && new Date(company.billing_expires) >= new Date()),
		hasTaxInfo: Boolean(
			(company.tax_address &&
				company.tax_address.company_code &&
				company.tax_address.address_line_1 &&
				company.tax_address.zip) ||
			company.tax_address?.has_tax_exclusion
		),
	};

	return (
		<BasePageTemplate title={`Billing - ${company?.name}`}>
			<Elements stripe={stripePromise}>
				<AdminContainer>
					<div className="space-y-6">
						<PageHeader company={company} userCompanyId={userCompanyId} />
						<CreditCardSection
							card={card}
							onOpenCardModal={handleOpenCardModal}
							isCreateCreditCardSetupIntentPending={isCreateCreditCardSetupIntentPending}
						/>
						{canAddSeats ? (
							<ActionSection
								canAddSeats={canAddSeats}
								onOpenCardModal={handleOpenCardModal}
								onOpenAddUsersModal={() => toggleModal("addUsers")}
								isCreateCreditCardSetupIntentPending={isCreateCreditCardSetupIntentPending}
							/>
						) : null}
						{charges.length > 0 ? <BillingHistory charges={charges} /> : null}
						{canAddSeats?.hasValidPricing && company ? (
							<AddUsersModal
								isOpen={modals.addUsers}
								onClose={() => toggleModal("addUsers")}
								company={company}
							/>
						) : null}
						{company && setupIntent ? (
							<CreditCardModal
								isOpen={Boolean(modals.creditCard && setupIntent)}
								onClose={handleCloseCardModal}
								setupIntent={setupIntent}
							/>
						) : null}
					</div>
				</AdminContainer>
			</Elements>
		</BasePageTemplate>
	);
};

export default Billing;
